/* ============================================
   THEME.JS — Y2K Trendy Fashion Store
   ============================================ */

'use strict';

// ============================================
// UTILITY HELPERS
// ============================================

const $ = (selector, ctx = document) => ctx.querySelector(selector);
const $$ = (selector, ctx = document) => [...ctx.querySelectorAll(selector)];

function openOverlay() {
  $('#overlay')?.classList.add('is-active');
  document.body.style.overflow = 'hidden';
}

function closeOverlay() {
  $('#overlay')?.classList.remove('is-active');
  document.body.style.overflow = '';
}

// ============================================
// HEADER — sticky + scroll shadow
// ============================================

(function initHeader() {
  const header = $('#site-header');
  if (!header) return;

  const onScroll = () => {
    header.classList.toggle('scrolled', window.scrollY > 10);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

// ============================================
// MOBILE NAV
// ============================================

(function initMobileNav() {
  const toggle = $('#menu-toggle');
  const nav    = $('#mobile-nav');
  const close  = $('#mobile-nav-close');
  const overlay = $('#overlay');
  if (!toggle || !nav) return;

  function openNav() {
    nav.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    openOverlay();
  }

  function closeNav() {
    nav.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    closeOverlay();
  }

  toggle.addEventListener('click', openNav);
  close?.addEventListener('click', closeNav);
  overlay?.addEventListener('click', closeNav);

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeNav();
  });
})();

// ============================================
// SEARCH BAR
// ============================================

(function initSearch() {
  const toggleBtn = $('#search-toggle');
  const closeBtn  = $('#search-close');
  const bar       = $('#search-bar');
  const input     = $('#search-bar-input');
  if (!toggleBtn || !bar) return;

  function openSearch() {
    bar.classList.add('is-open');
    bar.setAttribute('aria-hidden', 'false');
    toggleBtn.setAttribute('aria-expanded', 'true');
    setTimeout(() => input?.focus(), 50);
  }

  function closeSearch() {
    bar.classList.remove('is-open');
    bar.setAttribute('aria-hidden', 'true');
    toggleBtn.setAttribute('aria-expanded', 'false');
  }

  toggleBtn.addEventListener('click', () => {
    bar.classList.contains('is-open') ? closeSearch() : openSearch();
  });
  closeBtn?.addEventListener('click', closeSearch);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSearch(); });
})();

// ============================================
// CART DRAWER — Ajax cart
// ============================================

const CartDrawer = (() => {
  const drawer   = $('#cart-drawer');
  const openBtns = $$('[id="cart-toggle"], .js-cart-open');
  const overlay  = $('#overlay');
  const count    = $('#cart-count'); // header badge — never replaced

  let isOpen = false;
  let isBusy = false;

  // Re-query after every re-render (don't cache stale refs)
  const q = (sel) => drawer?.querySelector(sel);

  function setLoading(on) {
    if (!drawer) return;
    isBusy = on;
    const loader = q('.cart-drawer__loader');
    if (loader) loader.style.display = on ? 'flex' : 'none';
  }

  function open() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    isOpen = true;
    openOverlay();
    drawer.focus();
  }

  function close() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    isOpen = false;
    closeOverlay();
  }

  function toggle() { isOpen ? close() : open(); }

  function updateCounts(itemCount, totalPrice) {
    if (count) { count.textContent = itemCount; count.style.display = itemCount > 0 ? '' : 'none'; }
    const dc = q('#cart-drawer-count');
    const st = q('#cart-subtotal');
    if (dc) dc.textContent = itemCount;
    if (st) st.textContent = formatMoney(totalPrice);
  }

  // Unified re-render: replace entire drawer content from fresh server HTML
  async function rerenderDrawer() {
    const cartRes = await fetch(`/?section_id=cart-drawer&_t=${Date.now()}`);
    if (!cartRes.ok) return;
    const html = await cartRes.text();
    const doc  = new DOMParser().parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('#cart-drawer');
    if (!drawer || !newDrawer) return;
    const wasOpen = drawer.classList.contains('is-open');
    drawer.innerHTML = newDrawer.innerHTML;
    // Restore open state and show loader again (re-render hides it)
    if (wasOpen) drawer.classList.add('is-open');
    if (isBusy) { const loader = q('.cart-drawer__loader'); if (loader) loader.style.display = 'flex'; }
  }

  async function refresh() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      updateCounts(cart.item_count, cart.total_price);
    } catch (_) {}
  }

  async function addItem(variantId, quantity = 1, properties = {}) {
    if (isBusy) return false;
    const pageBtn = $('#add-to-cart-btn');
    if (pageBtn) { pageBtn.classList.add('is-loading'); pageBtn.disabled = true; }
    setLoading(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity, properties }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.description || 'Could not add to cart'); }
      await rerenderDrawer();
      await refresh();

      // Success state on ATC buttons
      ['.js-add-to-cart', '.js-sticky-atc'].forEach(function(sel) {
        const btn = document.querySelector(sel);
        if (!btn) return;
        const origLabel = btn.textContent;
        btn.textContent = '✓ ADDED';
        btn.classList.add('is-added');
        setTimeout(function() {
          btn.textContent = origLabel;
          btn.classList.remove('is-added');
        }, 1500);
      });

      open();
      return true;
    } catch (err) {
      showToast(err.message || 'Something went wrong', 'error');
      return false;
    } finally {
      setLoading(false);
      if (pageBtn) { pageBtn.classList.remove('is-loading'); pageBtn.disabled = false; }
    }
  }

  async function updateItem(key, quantity) {
    if (isBusy) return;
    setLoading(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error('Update failed');
      await rerenderDrawer();
      await refresh();
    } catch (_) {
      showToast('Could not update cart', 'error');
    } finally {
      setLoading(false);
    }
  }

  // Event delegation on drawer — survives innerHTML replacements
  function init() {
    openBtns.forEach((btn) => btn.addEventListener('click', toggle));
    overlay?.addEventListener('click', () => { if (isOpen) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen) close(); });

    drawer?.addEventListener('click', (e) => {
      if (e.target.closest('#cart-drawer-close')) { close(); return; }
      if (isBusy) return;

      const minus  = e.target.closest('.js-qty-minus');
      const plus   = e.target.closest('.js-qty-plus');
      const remove = e.target.closest('.js-remove-item');

      if (minus) {
        const key = minus.dataset.key;
        const cur = parseInt(minus.closest('.qty-control')?.querySelector('.qty-value')?.textContent || '1');
        updateItem(key, Math.max(0, cur - 1));
      } else if (plus) {
        const key = plus.dataset.key;
        const cur = parseInt(plus.closest('.qty-control')?.querySelector('.qty-value')?.textContent || '1');
        updateItem(key, cur + 1);
      } else if (remove) {
        updateItem(remove.dataset.key, 0);
      }
    });
  }

  return { init, open, close, addItem, refresh };
})();

CartDrawer.init();

// ============================================
// PRODUCT PAGE — variants, gallery, accordion
// ============================================

(function initProductPage() {
  const productData = window.productVariants;
  if (!productData) return;

  const variantIdInput = $('#variant-id-input');
  const priceEl        = $('#product-price');
  const comparePriceEl = $('#product-compare-price');
  const addToCartBtn   = $('#add-to-cart-btn');

  let selectedOptions = [];

  // Init selected options from the variant Liquid already rendered (matched by hidden input value)
  const currentId = parseInt(variantIdInput?.value);
  const initialVariant = productData.find(v => v.id === currentId)
    || productData.find(v => v.available)
    || productData[0];
  if (initialVariant) {
    selectedOptions = [...initialVariant.options];
  }

  function findVariant(options) {
    return productData.find(v =>
      v.options.every((opt, i) => opt === options[i])
    );
  }

  function updateVariantUI(variant) {
    if (!variant) return;

    // Update hidden input
    if (variantIdInput) variantIdInput.value = variant.id;

    // Update price
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    const offBadge = document.getElementById('product-off-badge');
    if (comparePriceEl) {
      if (variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        comparePriceEl.style.display = '';
        priceEl?.classList.add('pdp-price--sale');
        if (offBadge) {
          const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          offBadge.textContent = pct + '% OFF';
          offBadge.style.display = '';
        }
      } else {
        comparePriceEl.style.display = 'none';
        priceEl?.classList.remove('pdp-price--sale');
        if (offBadge) offBadge.style.display = 'none';
      }
    }

    // Update add to cart button
    if (addToCartBtn) {
      addToCartBtn.disabled = !variant.available;
      addToCartBtn.textContent = variant.available ? 'Add to Bag ✦' : 'Sold Out';
    }

    // Update URL without reload
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  // Color swatches — update label, variant, and scroll gallery to matching image
  $$('.js-color-swatch').forEach((swatch) => {
    swatch.addEventListener('click', () => {
      const idx       = parseInt(swatch.dataset.optionIndex);
      const value     = swatch.dataset.value;
      const variantId = swatch.dataset.variantId;
      const mediaId   = swatch.dataset.mediaId;

      $$('.js-color-swatch').forEach(s => s.classList.toggle('is-active', s === swatch));
      const colorLabel = $('#selected-color');
      if (colorLabel) colorLabel.textContent = value;

      // Scroll gallery grid to the matching image on mobile, or highlight on desktop
      if (mediaId) {
        const cells = Array.from($$('.js-gallery-cell'));
        const matchIdx = cells.findIndex(c => c.dataset.mediaId === mediaId);
        if (matchIdx >= 0) {
          const grid = document.getElementById('pdp-gallery-grid');
          const targetCell = cells[matchIdx];
          // Crossfade: fade out, update, fade back in
          targetCell.classList.add('is-fading');
          setTimeout(function() {
            if (grid && window.innerWidth < 768) {
              // Mobile: scroll to that slide
              grid.scrollTo({ left: matchIdx * grid.offsetWidth, behavior: 'smooth' });
            } else if (grid) {
              // Desktop: subtle highlight pulse
              targetCell.style.outline = '3px solid var(--color-primary)';
              targetCell.style.outlineOffset = '2px';
              targetCell.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              setTimeout(() => { targetCell.style.outline = ''; targetCell.style.outlineOffset = ''; }, 1200);
            }
            targetCell.classList.remove('is-fading');
          }, 220);
        }
      }

      selectedOptions[idx] = value;
      updateVariantUI(findVariant(selectedOptions));
    });
  });

  // Size buttons
  $$('.js-size-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx   = parseInt(btn.dataset.optionIndex);
      const value = btn.dataset.value;

      $$('.js-size-btn').forEach(b => {
        b.classList.toggle('is-active', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });

      const sizeLabel = $('#selected-size');
      if (sizeLabel) sizeLabel.textContent = value;

      selectedOptions[idx] = value;
      updateVariantUI(findVariant(selectedOptions));
    });
  });

  // Add to cart form submission
  const productForm = document.querySelector(`form[id^="product-form-"]`);
  if (productForm) {
    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const derivedVariant = findVariant(selectedOptions);
      const id  = derivedVariant ? String(derivedVariant.id) : variantIdInput?.value;
      const qty = parseInt($('#product-qty')?.value || 1);
      if (!id) return;
      await CartDrawer.addItem(id, qty);
    });
  }

  // Buy it now — add to cart then redirect straight to checkout
  $('.js-buy-now')?.addEventListener('click', async () => {
    const derivedVariant = findVariant(selectedOptions);
    const id  = derivedVariant ? String(derivedVariant.id) : variantIdInput?.value;
    const qty = parseInt($('#product-qty')?.value || 1);
    if (!id) return;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity: qty }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add to cart');
      }
      window.location.href = '/checkout';
    } catch (err) {
      showToast(err.message || 'Something went wrong', 'error');
    }
  });

  // Product quantity controls
  const qtyInput = $('#product-qty');
  $('.js-product-qty-minus')?.addEventListener('click', () => {
    if (!qtyInput) return;
    qtyInput.value = Math.max(1, parseInt(qtyInput.value) - 1);
  });
  $('.js-product-qty-plus')?.addEventListener('click', () => {
    if (!qtyInput) return;
    qtyInput.value = parseInt(qtyInput.value) + 1;
  });

  // Gallery image clicks on desktop open lightbox (handled inline in template JS)

  // Accordion
  $$('.accordion-trigger').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      const content    = $('#' + trigger.getAttribute('aria-controls'));

      // Close all
      $$('.accordion-trigger').forEach(t => t.setAttribute('aria-expanded', 'false'));
      $$('.accordion-content').forEach(c => c.classList.remove('is-open'));

      // Open clicked if it was closed
      if (!isExpanded) {
        trigger.setAttribute('aria-expanded', 'true');
        content?.classList.add('is-open');
      }
    });
  });
})();

// ============================================
// QUICK-ADD from product cards
// ============================================

const SPINNER_SVG = '<span class="pc-spinner" aria-hidden="true"></span>';
const CHECK_SVG   = '<svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.js-quick-add');
  if (!btn || btn.disabled) return;

  const variantId = btn.dataset.variantId;
  const productId = btn.dataset.productId;

  if (!variantId) {
    window.location.href = `/products/${productId}`;
    return;
  }

  // Capture original button content
  const originalHTML = btn.innerHTML;

  // ── Loading state ──
  btn.disabled = true;
  btn.classList.add('is-loading');
  btn.innerHTML = SPINNER_SVG;

  const success = await CartDrawer.addItem(variantId, 1);

  if (success) {
    // ── Success state ──
    btn.classList.remove('is-loading');
    btn.classList.add('is-success');
    btn.innerHTML = CHECK_SVG + ' Added!';

    setTimeout(() => {
      btn.innerHTML = originalHTML;
      btn.classList.remove('is-success');
      btn.disabled = false;
    }, 1800);
  } else {
    // ── Error — reset immediately (toast already shown by addItem) ──
    btn.innerHTML = originalHTML;
    btn.classList.remove('is-loading');
    btn.disabled = false;
  }
});

// ============================================
// SIZE GUIDE MODAL
// ============================================

(function initSizeGuide() {
  const modal   = $('#size-guide-modal');
  const openBtn = $('#open-size-guide');
  const closeBtn = $('#size-guide-close');
  const overlay  = $('#overlay');

  if (!modal) return;

  function open() {
    modal.classList.add('is-open');
    openOverlay();
  }

  function close() {
    modal.classList.remove('is-open');
    closeOverlay();
  }

  openBtn?.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
})();

// ============================================
// NEWSLETTER POPUP
// ============================================

(function initNewsletterPopup() {
  const popup    = $('#newsletter-popup');
  const closeBtn = $('#newsletter-popup-close');
  const skipBtn  = $('#newsletter-popup-skip');

  if (!popup) return;

  const DISMISSED_KEY = 'newsletter_dismissed';
  const SHOWN_KEY     = 'newsletter_shown';

  function dismiss() {
    popup.classList.remove('is-visible');
    sessionStorage.setItem(DISMISSED_KEY, '1');
  }

  function show() {
    popup.classList.add('is-visible');
    sessionStorage.setItem(SHOWN_KEY, '1');
  }

  // Don't show if already dismissed or submitted
  if (sessionStorage.getItem(DISMISSED_KEY) || localStorage.getItem('newsletter_subscribed')) {
    return;
  }

  // Show after 4 seconds on first visit
  const delay = sessionStorage.getItem(SHOWN_KEY) ? 0 : 4000;
  setTimeout(show, delay);

  const backdrop = $('#newsletter-popup-backdrop');

  closeBtn?.addEventListener('click', dismiss);
  skipBtn?.addEventListener('click', dismiss);
  backdrop?.addEventListener('click', dismiss);

  // Mark as subscribed on form submit
  const form = $('#newsletter-popup-form');
  form?.addEventListener('submit', () => {
    localStorage.setItem('newsletter_subscribed', '1');
    setTimeout(dismiss, 3000);
  });
})();

// ============================================
// SCROLL ANIMATIONS — fade-up
// ============================================

(function initScrollAnimations() {
  const els = $$('.fade-up');
  if (!els.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
  );

  els.forEach((el) => observer.observe(el));
})();

// ============================================
// SORT & FILTER (Collection page)
// ============================================

(function initCollectionFilters() {
  const sortSelect = $('#sort-select');
  if (!sortSelect) return;

  sortSelect.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.href = url.toString();
  });

  // Set current sort value from URL
  const currentSort = new URL(window.location.href).searchParams.get('sort_by');
  if (currentSort) sortSelect.value = currentSort;
})();

// ============================================
// TOAST NOTIFICATIONS
// ============================================

function showToast(message, type = 'success') {
  let container = $('#toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    document.body.appendChild(container);
  }

  const toast = document.createElement('div');
  toast.className = 'toast' + (type === 'error' ? ' toast--error' : '');
  toast.textContent = message;
  container.appendChild(toast);

  requestAnimationFrame(() => { toast.classList.add('is-visible'); });

  setTimeout(() => {
    toast.classList.add('is-hiding');
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

// ============================================
// WISHLIST (local state)
// ============================================

(function initWishlist() {
  const WISHLIST_KEY = 'wishlist_ids';

  function getWishlist() {
    try {
      const raw = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      // Migrate legacy string-ID arrays to {id, handle} objects
      return raw.map((item) => (typeof item === 'string' ? { id: item, handle: '' } : item));
    } catch { return []; }
  }

  function saveWishlist(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  }

  function toggleWishlist(productId, handle) {
    const items = getWishlist();
    const idx = items.findIndex((item) => item.id === productId);
    if (idx > -1) {
      items.splice(idx, 1);
      showToast('Removed from wishlist');
    } else {
      items.push({ id: productId, handle: handle || '' });
      showToast('Added to wishlist ♥');
    }
    saveWishlist(items);
    updateWishlistBtns();
  }

  function updateWishlistBtns() {
    const ids = getWishlist().map((item) => item.id);
    $$('.js-wishlist').forEach((btn) => {
      const wishlisted = ids.includes(btn.dataset.productId);
      btn.classList.toggle('is-wishlisted', wishlisted);
      btn.style.color = '';
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.js-wishlist');
    if (!btn) return;
    toggleWishlist(btn.dataset.productId, btn.dataset.productHandle);
  });

  updateWishlistBtns();
})();

// ============================================
// PRODUCT CARD — gallery dots + swatch label
// ============================================

// ── Swatch label (product card) ──────────────────────────
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.product-card').forEach(function (card) {
    var swatches  = card.querySelectorAll('.swatch[data-card]');
    var colorName = card.querySelector('.product-card__color-name');
    if (!swatches.length) return;

    swatches.forEach(function (sw) {
      sw.addEventListener('click', function (e) {
        e.stopPropagation();
        swatches.forEach(function (s) { s.classList.remove('swatch--active'); });
        this.classList.add('swatch--active');
        if (colorName) colorName.textContent = this.dataset.value || this.title;
      });
    });
  });
});

// ============================================
// UTILITY: format money
// ============================================

function formatMoney(cents) {
  const amount = Math.round(cents / 100).toLocaleString('en-IN');
  const fmt = (window.moneyFormat || '₹{{amount}}')
    .replace('{{amount_with_comma_separator}}', amount)
    .replace('{{amount_no_decimals_with_comma_separator}}', amount)
    .replace('{{amount_no_decimals}}', amount)
    .replace('{{amount}}', amount)
    .replace(/Rs\.?\s*/i, '₹')
    .replace(/INR\s*/i, '₹');
  return fmt;
}

// ============================================
// IMAGE LOADING PLACEHOLDERS
// ============================================
(function () {
  function hidePlaceholder(container) {
    const ph = container.querySelector('.img-placeholder');
    if (ph) ph.classList.add('is-hidden');
  }

  function watchContainer(container, imgSelector) {
    const img = container.querySelector(imgSelector);
    if (!img) { hidePlaceholder(container); return; }
    if (img.complete && img.naturalWidth > 0) {
      hidePlaceholder(container);
    } else {
      img.addEventListener('load', function () { hidePlaceholder(container); }, { once: true });
      img.addEventListener('error', function () { hidePlaceholder(container); }, { once: true });
    }
  }

  function initPlaceholders() {
    document.querySelectorAll('.product-card__media').forEach(function (el) {
      watchContainer(el, '.product-card__img:not(.product-card__img--hover)');
    });
    document.querySelectorAll('.pdp-gallery__cell').forEach(function (el) {
      watchContainer(el, '.pdp-gallery__img');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPlaceholders);
  } else {
    initPlaceholders();
  }

  // Re-run after cart drawer or quick-view injects new cards
  document.addEventListener('cart:updated', initPlaceholders);
})();
